/**
 * Utility functions for extracting lot images from background using SVG region coordinates
 */

/**
 * Get bounding box of a Three.js shape
 * @param {THREE.Shape} shape - The Three.js shape
 * @returns {Object} Bounding box with minX, minY, maxX, maxY
 */
export function getShapeBounds(shape) {
  const points = shape.getPoints()
  if (points.length === 0) return null
  
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  
  points.forEach(point => {
    minX = Math.min(minX, point.x)
    minY = Math.min(minY, point.y)
    maxX = Math.max(maxX, point.x)
    maxY = Math.max(maxY, point.y)
  })
  
  return { minX, minY, maxX, maxY }
}

/**
 * Convert SVG coordinates to image coordinates
 * @param {Object} svgBounds - SVG viewBox bounds
 * @param {Object} shapeBounds - Shape bounds in SVG coordinates
 * @param {Object} imageSize - Background image dimensions
 * @returns {Object} Image coordinates for cropping
 */
export function svgToImageCoords(svgBounds, shapeBounds, imageSize) {
  if (!svgBounds || !shapeBounds || !imageSize) return null
  
  // Calculate scale factors
  const scaleX = imageSize.width / svgBounds.width
  const scaleY = imageSize.height / svgBounds.height
  
  // Convert SVG coordinates to image coordinates
  const imageX = (shapeBounds.minX - svgBounds.minX) * scaleX
  const imageY = (shapeBounds.minY - svgBounds.minY) * scaleY
  const imageWidth = (shapeBounds.maxX - shapeBounds.minX) * scaleX
  const imageHeight = (shapeBounds.maxY - shapeBounds.minY) * scaleY
  
  return {
    x: Math.max(0, Math.floor(imageX)),
    y: Math.max(0, Math.floor(imageY)),
    width: Math.min(imageSize.width - Math.floor(imageX), Math.ceil(imageWidth)),
    height: Math.min(imageSize.height - Math.floor(imageY), Math.ceil(imageHeight))
  }
}

/**
 * Create a cropped image from background image using region bounds
 * @param {HTMLImageElement} backgroundImage - The background image
 * @param {Object} cropCoords - Cropping coordinates
 * @param {number} maxSize - Maximum size for the cropped image (for performance)
 * @returns {Promise<string>} Data URL of the cropped image
 */
export function cropImageFromBackground(backgroundImage, cropCoords, maxSize = 200) {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      if (!ctx) {
        reject(new Error('Canvas context not available'))
        return
      }
      
      // Set canvas size (scaled down for performance)
      const scale = Math.min(1, maxSize / Math.max(cropCoords.width, cropCoords.height))
      canvas.width = Math.floor(cropCoords.width * scale)
      canvas.height = Math.floor(cropCoords.height * scale)
      
      // Draw the cropped portion of the background image
      ctx.drawImage(
        backgroundImage,
        cropCoords.x, cropCoords.y, cropCoords.width, cropCoords.height,
        0, 0, canvas.width, canvas.height
      )
      
      // Convert to data URL
      const dataURL = canvas.toDataURL('image/jpeg', 0.95)
      resolve(dataURL)
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Create a shape-aware cropped image from background image using actual shape geometry
 * @param {HTMLImageElement} backgroundImage - The background image
 * @param {Object} svgBounds - SVG viewBox bounds
 * @param {THREE.Shape} shape - The lot shape
 * @param {Object} cropCoords - Bounding box coordinates for initial crop
 * @param {number} maxSize - Maximum size for the cropped image (for performance)
 * @returns {Promise<string>} Data URL of the cropped image
 */
export function cropImageWithShape(backgroundImage, svgBounds, shape, cropCoords, maxSize = 200) {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      if (!ctx) {
        reject(new Error('Canvas context not available'))
        return
      }
      
      // Set canvas size (scaled down for performance)
      const scale = Math.min(1, maxSize / Math.max(cropCoords.width, cropCoords.height))
      canvas.width = Math.floor(cropCoords.width * scale)
      canvas.height = Math.floor(cropCoords.height * scale)
      
      // Fill canvas with white background to avoid black background
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      
      // Calculate scale factors for coordinate conversion
      const scaleX = canvas.width / cropCoords.width
      const scaleY = canvas.height / cropCoords.height
      
      // Get shape points and convert to canvas coordinates
      const shapePoints = shape.getPoints()
      if (!shapePoints || shapePoints.length === 0) {
        console.warn('No shape points available, falling back to rectangular crop')
        // Fallback to rectangular crop if no shape points
        // White background already filled above, just draw image
        ctx.drawImage(
          backgroundImage,
          cropCoords.x, cropCoords.y, cropCoords.width, cropCoords.height,
          0, 0, canvas.width, canvas.height
        )
        const dataURL = canvas.toDataURL('image/jpeg', 0.8)
        resolve(dataURL)
        return
      }
      
      const canvasPoints = shapePoints.map(point => {
        // Convert from SVG coordinates to canvas coordinates
        // First convert SVG point to image coordinates
        const imageX = (point.x - svgBounds.minX) * (backgroundImage.naturalWidth / svgBounds.width)
        const imageY = (point.y - svgBounds.minY) * (backgroundImage.naturalHeight / svgBounds.height)
        
        // Then convert to relative coordinates within the crop area
        const relativeX = imageX - cropCoords.x
        const relativeY = imageY - cropCoords.y
        
        // Finally scale to canvas coordinates
        return {
          x: relativeX * scaleX,
          y: relativeY * scaleY
        }
      })
      
      // Validate canvas points
      const validPoints = canvasPoints.filter(p => 
        !isNaN(p.x) && !isNaN(p.y) && 
        isFinite(p.x) && isFinite(p.y)
      )
      
      // console.log('Shape-aware cropping:', {
      //   shapePoints: shapePoints.length,
      //   validPoints: validPoints.length,
      //   canvasSize: `${canvas.width}x${canvas.height}`,
      //   cropCoords: cropCoords,
      //   samplePoints: validPoints.slice(0, 3)
      // })
      
      if (validPoints.length < 3) {
        console.warn('Insufficient valid shape points, falling back to rectangular crop')
        // Fallback to rectangular crop if insufficient points
        ctx.drawImage(
          backgroundImage,
          cropCoords.x, cropCoords.y, cropCoords.width, cropCoords.height,
          0, 0, canvas.width, canvas.height
        )
        const dataURL = canvas.toDataURL('image/jpeg', 0.8)
        resolve(dataURL)
        return
      }
      
      // Check if the shape points create a reasonable clipping area
      const bounds = {
        minX: Math.min(...validPoints.map(p => p.x)),
        maxX: Math.max(...validPoints.map(p => p.x)),
        minY: Math.min(...validPoints.map(p => p.y)),
        maxY: Math.max(...validPoints.map(p => p.y))
      }
      
      const shapeArea = (bounds.maxX - bounds.minX) * (bounds.maxY - bounds.minY)
      const canvasArea = canvas.width * canvas.height
      const shapeRatio = shapeArea / canvasArea
      
      // console.log('Shape bounds:', bounds, 'Shape ratio:', shapeRatio.toFixed(3))
      
      if (shapeRatio < 0.01) {
        console.warn('Shape area too small, using rectangular crop')
        // Fallback to rectangular crop if shape is too small
        ctx.drawImage(
          backgroundImage,
          cropCoords.x, cropCoords.y, cropCoords.width, cropCoords.height,
          0, 0, canvas.width, canvas.height
        )
      } else {
        // Create clipping path from shape points for accurate shape-aware cropping
        ctx.beginPath()
        ctx.moveTo(validPoints[0].x, validPoints[0].y)
        for (let i = 1; i < validPoints.length; i++) {
          ctx.lineTo(validPoints[i].x, validPoints[i].y)
        }
        ctx.closePath()
        
        // Set clipping path
        ctx.clip()
        
        // Draw the cropped portion of the background image
        ctx.drawImage(
          backgroundImage,
          cropCoords.x, cropCoords.y, cropCoords.width, cropCoords.height,
          0, 0, canvas.width, canvas.height
        )
        
        // Optional: Draw shape outline for debugging (uncomment to enable)
        // ctx.save()
        // ctx.restore() // This clears the clipping path
        // ctx.strokeStyle = 'red'
        // ctx.lineWidth = 2
        // ctx.beginPath()
        // ctx.moveTo(validPoints[0].x, validPoints[0].y)
        // for (let i = 1; i < validPoints.length; i++) {
        //   ctx.lineTo(validPoints[i].x, validPoints[i].y)
        // }
        // ctx.closePath()
        // ctx.stroke()
      }
      
      // Convert to data URL
      const dataURL = canvas.toDataURL('image/jpeg', 0.95)
      resolve(dataURL)
    } catch (error) {
      console.warn('Shape-aware cropping failed, falling back to rectangular crop:', error)
      // Fallback to rectangular crop on any error
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        
        const scale = Math.min(1, maxSize / Math.max(cropCoords.width, cropCoords.height))
        canvas.width = Math.floor(cropCoords.width * scale)
        canvas.height = Math.floor(cropCoords.height * scale)
        
        // Fill canvas with white background
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        
        ctx.drawImage(
          backgroundImage,
          cropCoords.x, cropCoords.y, cropCoords.width, cropCoords.height,
          0, 0, canvas.width, canvas.height
        )
        
        const dataURL = canvas.toDataURL('image/jpeg', 0.8)
        resolve(dataURL)
      } catch (fallbackError) {
        reject(fallbackError)
      }
    }
  })
}

/**
 * Cache for lot images to prevent repeated processing
 */
const lotImageCache = new Map()

/**
 * Get or create a lot image from background
 * @param {string} cacheKey - Unique key for caching
 * @param {HTMLImageElement} backgroundImage - The background image
 * @param {Object} svgBounds - SVG viewBox bounds
 * @param {THREE.Shape} shape - The lot shape
 * @param {number} maxSize - Maximum size for the cropped image
 * @returns {Promise<string>} Data URL of the lot image
 */
export async function getLotImage(cacheKey, backgroundImage, svgBounds, shape, maxSize = 200) {
  // Check cache first
  if (lotImageCache.has(cacheKey)) {
    // console.log('Using cached image for:', cacheKey.substring(0, 30))
    return lotImageCache.get(cacheKey)
  }
  
  try {
    // console.log('Processing new image extraction for:', cacheKey.substring(0, 30))
    
    // Get shape bounds
    const shapeBounds = getShapeBounds(shape)
    if (!shapeBounds) {
      throw new Error('Invalid shape bounds')
    }
    
    // Convert to image coordinates
    const imageSize = {
      width: backgroundImage.naturalWidth,
      height: backgroundImage.naturalHeight
    }
    
    const cropCoords = svgToImageCoords(svgBounds, shapeBounds, imageSize)
    if (!cropCoords || cropCoords.width <= 0 || cropCoords.height <= 0) {
      throw new Error('Invalid crop coordinates')
    }
    
    // Use shape-aware cropping for accurate irregular shapes
    const dataURL = await cropImageWithShape(backgroundImage, svgBounds, shape, cropCoords, maxSize)
    
    // console.log('Generated image:', dataURL ? `${dataURL.length} chars` : 'null')
    
    // Cache the result
    lotImageCache.set(cacheKey, dataURL)
    
    return dataURL
  } catch (error) {
    console.warn('Failed to extract lot image:', error)
    return null
  }
}

/**
 * Clear the lot image cache
 */
export function clearLotImageCache() {
  lotImageCache.clear()
}

/**
 * Get cache size for debugging
 */
export function getCacheSize() {
  return lotImageCache.size
}

/**
 * Get combined bounds for multiple shapes (e.g., all lots in a stage)
 * @param {Array<THREE.Shape>} shapes - Array of Three.js shapes
 * @returns {Object} Combined bounding box with minX, minY, maxX, maxY
 */
export function getCombinedShapeBounds(shapes) {
  if (!shapes || shapes.length === 0) return null
  
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  
  shapes.forEach(shape => {
    const bounds = getShapeBounds(shape)
    if (bounds) {
      minX = Math.min(minX, bounds.minX)
      minY = Math.min(minY, bounds.minY)
      maxX = Math.max(maxX, bounds.maxX)
      maxY = Math.max(maxY, bounds.maxY)
    }
  })
  
  if (minX === Infinity) return null
  
  return { minX, minY, maxX, maxY }
}

/**
 * Get status icon/symbol
 * @param {string} status - Status (AVAILABLE, SOLD, LOCKED)
 * @returns {string} Icon symbol
 */
function getStatusIcon(status) {
  switch (status) {
    case 'AVAILABLE':
      return '✓' // Checkmark
    case 'SOLD':
      return '✗' // X mark
    case 'LOCKED':
      return '🔒' // Lock
    default:
      return '•' // Dot
  }
}

/**
 * Draw status badge on canvas with multiple display options
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} x - X position in canvas coordinates
 * @param {number} y - Y position in canvas coordinates
 * @param {string} status - Status text (AVAILABLE, SOLD, LOCKED)
 * @param {string} statusColor - Color for the badge
 * @param {number} scale - Scale factor for badge size
 * @param {Object} options - Display options { showIcon, showText, showLotNumber, lotNumber }
 */
function drawStatusBadge(ctx, x, y, status, statusColor, scale = 1, options = {}) {
  const { showIcon = true, showText = true, showLotNumber = true, lotNumber = null } = options
  const fontSize = Math.max(12, 14 * scale)
  const iconSize = fontSize * 1.2
  const padding = 6 * scale
  const borderRadius = 4 * scale
  
  ctx.save()
  
  // Build display text
  let displayText = ''
  if (showLotNumber && lotNumber) {
    displayText = `Lot ${lotNumber}`
    if (showText) {
      displayText += `\n${status || 'LOCKED'}`
    }
  } else if (showText) {
    displayText = status || 'LOCKED'
  }
  
  // Set font
  ctx.font = `bold ${fontSize}px Arial, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  
  // Measure text
  const lines = displayText.split('\n')
  const textMetrics = lines.map(line => ctx.measureText(line))
  const maxTextWidth = Math.max(...textMetrics.map(m => m.width))
  const textHeight = fontSize * lines.length + (lines.length > 1 ? fontSize * 0.3 : 0)
  
  // Calculate badge dimensions
  let badgeWidth = maxTextWidth + padding * 2
  let badgeHeight = textHeight + padding * 2
  
  // Add space for icon if showing
  if (showIcon) {
    badgeWidth += iconSize + padding
  }
  
  const badgeX = x - badgeWidth / 2
  const badgeY = y - badgeHeight / 2
  
  // Draw badge background with rounded corners
  ctx.fillStyle = statusColor || '#D3D3D3'
  ctx.beginPath()
  ctx.moveTo(badgeX + borderRadius, badgeY)
  ctx.lineTo(badgeX + badgeWidth - borderRadius, badgeY)
  ctx.quadraticCurveTo(badgeX + badgeWidth, badgeY, badgeX + badgeWidth, badgeY + borderRadius)
  ctx.lineTo(badgeX + badgeWidth, badgeY + badgeHeight - borderRadius)
  ctx.quadraticCurveTo(badgeX + badgeWidth, badgeY + badgeHeight, badgeX + badgeWidth - borderRadius, badgeY + badgeHeight)
  ctx.lineTo(badgeX + borderRadius, badgeY + badgeHeight)
  ctx.quadraticCurveTo(badgeX, badgeY + badgeHeight, badgeX, badgeY + badgeHeight - borderRadius)
  ctx.lineTo(badgeX, badgeY + borderRadius)
  ctx.quadraticCurveTo(badgeX, badgeY, badgeX + borderRadius, badgeY)
  ctx.closePath()
  ctx.fill()
  
  // Draw icon if enabled
  if (showIcon) {
    const icon = getStatusIcon(status)
    ctx.fillStyle = '#FFFFFF'
    ctx.font = `${iconSize}px Arial, sans-serif`
    const iconX = badgeX + iconSize / 2 + padding
    ctx.fillText(icon, iconX, y)
  }
  
  // Draw text
  if (displayText) {
    ctx.fillStyle = '#FFFFFF'
    ctx.font = `bold ${fontSize}px Arial, sans-serif`
    let textX = x
    if (showIcon) {
      textX = badgeX + iconSize + padding * 2 + maxTextWidth / 2
    }
    
    lines.forEach((line, index) => {
      const lineY = y - (lines.length - 1) * fontSize * 0.3 / 2 + index * fontSize * 1.3
      ctx.fillText(line, textX, lineY)
    })
  }
  
  // Draw border
  ctx.strokeStyle = '#FFFFFF'
  ctx.lineWidth = 2 * scale
  ctx.stroke()
  
  ctx.restore()
}

/**
 * Draw colored border around lot shape
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {THREE.Shape} shape - The lot shape
 * @param {Object} svgBounds - SVG viewBox bounds
 * @param {Object} imageSize - Image dimensions
 * @param {Object} cropCoords - Crop coordinates
 * @param {string} statusColor - Color for the border
 * @param {number} scaleX - X scale factor
 * @param {number} scaleY - Y scale factor
 */
function drawLotBorder(ctx, shape, svgBounds, imageSize, cropCoords, statusColor, scaleX, scaleY) {
  const points = shape.getPoints()
  if (!points || points.length === 0) return
  
  ctx.save()
  ctx.strokeStyle = statusColor || '#D3D3D3'
  ctx.lineWidth = 3
  ctx.beginPath()
  
  points.forEach((point, index) => {
    // Convert SVG coordinates to image coordinates
    const imageX = (point.x - svgBounds.minX) * (imageSize.width / svgBounds.width)
    const imageY = (point.y - svgBounds.minY) * (imageSize.height / svgBounds.height)
    
    // Convert to canvas coordinates
    const canvasX = (imageX - cropCoords.x) * scaleX
    const canvasY = (imageY - cropCoords.y) * scaleY
    
    if (index === 0) {
      ctx.moveTo(canvasX, canvasY)
    } else {
      ctx.lineTo(canvasX, canvasY)
    }
  })
  
  ctx.closePath()
  ctx.stroke()
  ctx.restore()
}

/**
 * Draw colored overlay on lot shape
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {THREE.Shape} shape - The lot shape
 * @param {Object} svgBounds - SVG viewBox bounds
 * @param {Object} imageSize - Image dimensions
 * @param {Object} cropCoords - Crop coordinates
 * @param {string} statusColor - Color for the overlay
 * @param {number} scaleX - X scale factor
 * @param {number} scaleY - Y scale factor
 * @param {number} opacity - Overlay opacity (0-1), default 0.3
 */
function drawLotOverlay(ctx, shape, svgBounds, imageSize, cropCoords, statusColor, scaleX, scaleY, opacity = 0.3) {
  const points = shape.getPoints()
  if (!points || points.length === 0) return
  
  ctx.save()
  
  // Convert color to RGBA with opacity
  let overlayColor = statusColor || '#D3D3D3'
  
  // Handle different color formats
  if (overlayColor.startsWith('rgb(')) {
    // Convert rgb(r, g, b) to rgba(r, g, b, opacity)
    overlayColor = overlayColor.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`)
  } else if (overlayColor.startsWith('rgba(')) {
    // Already rgba, just update opacity
    overlayColor = overlayColor.replace(/,\s*[\d.]+\)$/, `, ${opacity})`)
  } else if (overlayColor.startsWith('#')) {
    // Convert hex to rgba
    const hex = overlayColor.replace('#', '')
    let r, g, b
    if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16)
      g = parseInt(hex.substring(2, 4), 16)
      b = parseInt(hex.substring(4, 6), 16)
    } else if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16)
      g = parseInt(hex[1] + hex[1], 16)
      b = parseInt(hex[2] + hex[2], 16)
    } else {
      // Fallback to gray
      r = g = b = 211
    }
    overlayColor = `rgba(${r}, ${g}, ${b}, ${opacity})`
  } else {
    // Fallback: use default gray with opacity
    overlayColor = `rgba(211, 211, 211, ${opacity})`
  }
  
  ctx.fillStyle = overlayColor
  ctx.beginPath()
  
  points.forEach((point, index) => {
    // Convert SVG coordinates to image coordinates
    const imageX = (point.x - svgBounds.minX) * (imageSize.width / svgBounds.width)
    const imageY = (point.y - svgBounds.minY) * (imageSize.height / svgBounds.height)
    
    // Convert to canvas coordinates
    const canvasX = (imageX - cropCoords.x) * scaleX
    const canvasY = (imageY - cropCoords.y) * scaleY
    
    if (index === 0) {
      ctx.moveTo(canvasX, canvasY)
    } else {
      ctx.lineTo(canvasX, canvasY)
    }
  })
  
  ctx.closePath()
  ctx.fill()
  ctx.restore()
}

/**
 * Crop image for a stage (all lots in that stage) or a single lot
 * @param {HTMLImageElement} backgroundImage - The background image
 * @param {Object} svgBounds - SVG viewBox bounds
 * @param {Object} shapeBounds - Combined shape bounds in SVG coordinates
 * @param {number} padding - Padding factor (1.0 = no padding, 1.25 = 25% padding)
 * @param {Array} regionsWithShapes - Optional array of {region, shape} objects to draw status badges
 * @param {string} activeStage - Optional active stage name to determine which lots to grey out
 * @param {Map|null} groupColorMap - Optional map of group colors
 * @param {HTMLImageElement|null} overlayImage - Optional overlay image to composite over the background
 * @param {Object|null} statusColors - Optional status color overrides
 * @returns {Promise<Blob>} Blob of the cropped image
 */
export async function cropImageForDownload(backgroundImage, svgBounds, shapeBounds, padding = 1.25, regionsWithShapes = null, activeStage = null, groupColorMap = null, overlayImage = null, statusColors = null) {
  return new Promise((resolve, reject) => {
    try {
      if (!backgroundImage || !shapeBounds) {
        reject(new Error('Missing required parameters'))
        return
      }
      
      // Ensure image is fully loaded
      if (!backgroundImage.complete || backgroundImage.naturalWidth === 0 || backgroundImage.naturalHeight === 0) {
        reject(new Error('Background image not fully loaded'))
        return
      }
      
      const imageSize = {
        width: backgroundImage.naturalWidth,
        height: backgroundImage.naturalHeight
      }
      
      
      // If svgBounds is not provided or invalid, we need to calculate it properly
      // The issue is that shape coordinates are in SVG space, but we need to map them to image space
      let effectiveSvgBounds = svgBounds
      if (!effectiveSvgBounds || !effectiveSvgBounds.width || !effectiveSvgBounds.height) {
        // If we don't have svgBounds, we can't properly convert coordinates
        // In this case, assume shape bounds are already in image pixel coordinates
        // This is a fallback that might not work correctly, but it's better than nothing
        console.warn('cropImageForDownload - No svgBounds provided, assuming shape bounds are in image pixel space')
        // Use shape bounds directly as image coordinates (no conversion needed)
        const paddedWidth = (shapeBounds.maxX - shapeBounds.minX) * padding
        const paddedHeight = (shapeBounds.maxY - shapeBounds.minY) * padding
        const centerX = shapeBounds.minX + (shapeBounds.maxX - shapeBounds.minX) / 2
        const centerY = shapeBounds.minY + (shapeBounds.maxY - shapeBounds.minY) / 2
        
        const cropCoords = {
          x: Math.max(0, Math.floor(centerX - paddedWidth / 2)),
          y: Math.max(0, Math.floor(centerY - paddedHeight / 2)),
          width: Math.min(imageSize.width, Math.ceil(paddedWidth)),
          height: Math.min(imageSize.height, Math.ceil(paddedHeight))
        }
        
        // Clamp to image bounds
        if (cropCoords.x + cropCoords.width > imageSize.width) {
          cropCoords.width = imageSize.width - cropCoords.x
        }
        if (cropCoords.y + cropCoords.height > imageSize.height) {
          cropCoords.height = imageSize.height - cropCoords.y
        }
        
        
        // Create canvas and draw directly
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        
        if (!ctx) {
          reject(new Error('Canvas context not available'))
          return
        }
        
        canvas.width = cropCoords.width
        canvas.height = cropCoords.height
        
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        
        ctx.drawImage(
          backgroundImage,
          cropCoords.x, cropCoords.y, cropCoords.width, cropCoords.height,
          0, 0, canvas.width, canvas.height
        )
        
        // Draw status badges if regionsWithShapes is provided (for direct coordinate mapping)
        if (regionsWithShapes && regionsWithShapes.length > 0) {
          // Import statusToColor - use dynamic import but handle it synchronously
          import('./status').then(statusModule => {
            const { statusToColor } = statusModule
            const getStatusColor = (status) => statusToColor(status, statusColors)
            const scaleX = canvas.width / cropCoords.width
            const scaleY = canvas.height / cropCoords.height
            const scale = Math.min(scaleX, scaleY)
            
            // Create a simple svgBounds for border drawing (using shape bounds directly)
            const allBounds = getCombinedShapeBounds(regionsWithShapes.map(({ shape }) => shape).filter(Boolean))
            const simpleSvgBounds = allBounds ? {
              minX: allBounds.minX,
              minY: allBounds.minY,
              width: allBounds.maxX - allBounds.minX,
              height: allBounds.maxY - allBounds.minY
            } : null
            
            regionsWithShapes.forEach(({ region, shape }) => {
              if (!region || !shape) return
              
              // Check if this lot belongs to the active stage
              const regionStage = (region?.name && String(region.name).trim()) || 'Ungrouped'
              const isActiveStage = !activeStage || regionStage === activeStage
              
            const status = region.status || 'LOCKED'
            let overlayColor, borderColor, badgeColor
            
              if (isActiveStage) {
                // Check if we should use group color for SOLD lots
                if (status === 'SOLD' && groupColorMap && region.customProps?.group) {
                  const groupColor = groupColorMap.get(region.customProps.group)
                  if (groupColor) {
                    overlayColor = groupColor
                    borderColor = groupColor
                    badgeColor = groupColor
                  } else {
                    overlayColor = getStatusColor(status)
                    borderColor = getStatusColor(status)
                    badgeColor = getStatusColor(status)
                  }
              }
              // Use status color for active stage lots
              // For AVAILABLE, use a brighter color for PDF visibility
              else if (status === 'AVAILABLE') {
                // Use a brighter green for AVAILABLE lots in PDF
                overlayColor = '#5CB85C' // Brighter green
                borderColor = '#5CB85C'
                badgeColor = '#5CB85C'
              } else {
                overlayColor = getStatusColor(status)
                borderColor = getStatusColor(status)
                badgeColor = getStatusColor(status)
              }
                
                // For SOLD and LOCKED, use higher opacity to hide background details
                // For AVAILABLE, use lower opacity but brighter color for PDF visibility
                const overlayOpacity = (status === 'SOLD' || status === 'LOCKED') ? 0.55 : 0.25
                
                // Draw colored overlay on lot (semi-transparent fill) - if we have bounds
                if (simpleSvgBounds) {
                  drawLotOverlay(ctx, shape, simpleSvgBounds, imageSize, cropCoords, overlayColor, scaleX, scaleY, overlayOpacity)
                }
                
                // Draw colored border around lot (if we have bounds)
                if (simpleSvgBounds) {
                  drawLotBorder(ctx, shape, simpleSvgBounds, imageSize, cropCoords, borderColor, scaleX, scaleY)
                }
                
                // No badges for any lots - removed per user request
              } else {
                // For lots from different stages: treat as LOCKED (for PDF only)
                overlayColor = getStatusColor('LOCKED')
                borderColor = getStatusColor('LOCKED')
                
                // Draw LOCKED overlay (same as LOCKED lots)
                if (simpleSvgBounds) {
                  drawLotOverlay(ctx, shape, simpleSvgBounds, imageSize, cropCoords, overlayColor, scaleX, scaleY, 0.8)
                }
                
                // Draw LOCKED border
                if (simpleSvgBounds) {
                  drawLotBorder(ctx, shape, simpleSvgBounds, imageSize, cropCoords, borderColor, scaleX, scaleY)
                }
                // No badges for lots from different stages
              }
            })
            
            // Convert to blob after drawing badges
            canvas.toBlob((blob) => {
              if (blob) {
                resolve(blob)
              } else {
                reject(new Error('Failed to create blob'))
              }
            }, 'image/jpeg', 0.95)
          }).catch(err => {
            console.warn('Failed to import status module, skipping badges:', err)
            // Convert to blob without badges
            canvas.toBlob((blob) => {
              if (blob) {
                resolve(blob)
              } else {
                reject(new Error('Failed to create blob'))
              }
            }, 'image/jpeg', 0.95)
          })
          return // Early return, blob creation handled in promise
        }
        
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to create blob'))
          }
        }, 'image/jpeg', 0.95)
        
        return // Early return, we've handled the crop
      }
      
      // Calculate bounds with padding
      const width = shapeBounds.maxX - shapeBounds.minX
      const height = shapeBounds.maxY - shapeBounds.minY
      const centerX = shapeBounds.minX + width / 2
      const centerY = shapeBounds.minY + height / 2
      
      const paddedWidth = width * padding
      const paddedHeight = height * padding
      
      const paddedBounds = {
        minX: centerX - paddedWidth / 2,
        minY: centerY - paddedHeight / 2,
        maxX: centerX + paddedWidth / 2,
        maxY: centerY + paddedHeight / 2
      }
      
      
      // Convert to image coordinates
      const cropCoords = svgToImageCoords(effectiveSvgBounds, paddedBounds, imageSize)
      if (!cropCoords || cropCoords.width <= 0 || cropCoords.height <= 0) {
        console.error('Invalid crop coordinates:', cropCoords)
        reject(new Error('Invalid crop coordinates'))
        return
      }
      
      
      // Validate crop coordinates are within image bounds
      if (cropCoords.x < 0 || cropCoords.y < 0 || 
          cropCoords.x + cropCoords.width > imageSize.width || 
          cropCoords.y + cropCoords.height > imageSize.height) {
        console.warn('Crop coordinates exceed image bounds, clamping...')
        cropCoords.x = Math.max(0, Math.min(cropCoords.x, imageSize.width - 1))
        cropCoords.y = Math.max(0, Math.min(cropCoords.y, imageSize.height - 1))
        cropCoords.width = Math.min(cropCoords.width, imageSize.width - cropCoords.x)
        cropCoords.height = Math.min(cropCoords.height, imageSize.height - cropCoords.y)
      }
      
      // Create canvas for full resolution (no maxSize limit for downloads)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      if (!ctx) {
        reject(new Error('Canvas context not available'))
        return
      }
      
      // Set canvas size to match crop area (full resolution)
      canvas.width = cropCoords.width
      canvas.height = cropCoords.height
      
      
      // Fill with white background first (in case of transparency or out of bounds)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      
      // Draw the cropped portion of the background image
      ctx.drawImage(
        backgroundImage,
        cropCoords.x, cropCoords.y, cropCoords.width, cropCoords.height,
        0, 0, canvas.width, canvas.height
      )
      
      const drawOverlayOnTop = () => {
        if (!overlayImage) return
        try {
          ctx.drawImage(
            overlayImage,
            cropCoords.x, cropCoords.y, cropCoords.width, cropCoords.height,
            0, 0, canvas.width, canvas.height
          )
        } catch (overlayErr) {
          console.warn('Failed to draw overlay image for export:', overlayErr)
        }
      }
      
      // Draw status badges if regionsWithShapes is provided
      if (regionsWithShapes && regionsWithShapes.length > 0 && effectiveSvgBounds) {
        // Import statusToColor - use dynamic import but handle it synchronously
        import('./status').then(statusModule => {
          const { statusToColor } = statusModule
          const getStatusColor = (status) => statusToColor(status, statusColors)
          const scaleX = canvas.width / cropCoords.width
          const scaleY = canvas.height / cropCoords.height
          const scale = Math.min(scaleX, scaleY)
          
          regionsWithShapes.forEach(({ region, shape }) => {
            if (!region || !shape) return
            
            // Check if this lot belongs to the active stage
            const regionStage = (region?.name && String(region.name).trim()) || 'Ungrouped'
            const isActiveStage = !activeStage || regionStage === activeStage
            
            const status = region.status || 'LOCKED'
            let overlayColor, borderColor, badgeColor
            
            if (isActiveStage) {
              // Check if we should use group color for SOLD lots
              if (status === 'SOLD' && groupColorMap && region.customProps?.group) {
                const groupColor = groupColorMap.get(region.customProps.group)
                if (groupColor) {
                  overlayColor = groupColor
                  borderColor = groupColor
                  badgeColor = groupColor
                } else {
                  overlayColor = getStatusColor(status)
                  borderColor = getStatusColor(status)
                  badgeColor = getStatusColor(status)
                }
              }
              // Use status color for active stage lots
              // For AVAILABLE, use a brighter color for PDF visibility
              else if (status === 'AVAILABLE') {
                // Use a brighter green for AVAILABLE lots in PDF
                overlayColor = '#5CB85C' // Brighter green
                borderColor = '#5CB85C'
                badgeColor = '#5CB85C'
              } else {
                overlayColor = getStatusColor(status)
                borderColor = getStatusColor(status)
                badgeColor = getStatusColor(status)
              }
              
              // For SOLD and LOCKED, use higher opacity to hide background details
              // For AVAILABLE, use lower opacity but brighter color for PDF visibility
              const overlayOpacity = (status === 'SOLD' || status === 'LOCKED') ? 0.55 : 0.25
              
              // Draw colored overlay on lot
              drawLotOverlay(ctx, shape, effectiveSvgBounds, imageSize, cropCoords, overlayColor, scaleX, scaleY, overlayOpacity)
              
              // Draw colored border around lot
              drawLotBorder(ctx, shape, effectiveSvgBounds, imageSize, cropCoords, borderColor, scaleX, scaleY)
              
              // No badges for any lots - removed per user request
            } else {
              // For lots from different stages: treat as LOCKED (for PDF only)
              overlayColor = getStatusColor('LOCKED')
              borderColor = getStatusColor('LOCKED')
              
              // Draw LOCKED overlay (same as LOCKED lots)
              drawLotOverlay(ctx, shape, effectiveSvgBounds, imageSize, cropCoords, overlayColor, scaleX, scaleY, 0.8)
              
              // Draw LOCKED border
              drawLotBorder(ctx, shape, effectiveSvgBounds, imageSize, cropCoords, borderColor, scaleX, scaleY)
              // No badges for lots from different stages
            }
          })
          
          drawOverlayOnTop()
          // Convert to blob after drawing badges and overlay
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to create blob'))
            }
          }, 'image/jpeg', 0.95)
        }).catch(err => {
          console.warn('Failed to import status module, skipping badges:', err)
          drawOverlayOnTop()
          // Convert to blob without badges but with overlay
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to create blob'))
            }
          }, 'image/jpeg', 0.95)
        })
        return // Early return, blob creation handled in promise
      }
      
      // No badges drawn: ensure overlay is on top and convert
      drawOverlayOnTop()
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to create blob'))
        }
      }, 'image/jpeg', 0.95)
    } catch (error) {
      console.error('cropImageForDownload - error:', error)
      reject(error)
    }
  })
}
